import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MOCK_ORDERS } from "../../../data/mockOrders";
import { ORDER_STATUSES, TIMELINE_STEPS } from "../../TrackOrder/trackOrderConstants";

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const statusClass = (status) => {
  if (status === "delivered") return "bg-primary-50 text-primary-700";
  if (status === "cancelled") return "bg-red-50 text-red-700";
  if (status === "processing") return "bg-blue-50 text-blue-700";
  if (status === "shipped") return "bg-amber-50 text-amber-700";
  return "bg-gray-100 text-gray-700";
};

const TrackOrderSection = () => {
  const orders = MOCK_ORDERS;
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchParams] = useSearchParams();
  const orderIdFromUrl = searchParams.get("orderId");

  useEffect(() => {
    if (orders.length === 0) return;
    if (orderIdFromUrl) {
      const found = orders.find(
        (order) => String(order.id || order._id) === orderIdFromUrl,
      );
      if (found) {
        setSelectedOrder(found);
        return;
      }
    }
    if (!selectedOrder) setSelectedOrder(orders[0]);
  }, [orders, orderIdFromUrl, selectedOrder]);

  const currentStep = useMemo(() => {
    if (!selectedOrder || selectedOrder.status === "cancelled") return 0;
    return ORDER_STATUSES[selectedOrder.status]?.step || 1;
  }, [selectedOrder]);

  return (
    <div>
      <div className="mb-5 border-b border-gray-200 pb-4">
        <h1 className="text-lg font-semibold text-gray-900">Track Order</h1>
        <p className="mt-1 text-sm text-gray-500">
          Select an order to view delivery progress.
        </p>
      </div>

      {orders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-12 text-center text-sm text-gray-500">
          You have no orders to track.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[320px_minmax(0,1fr)]">
          <div className="rounded-lg border border-gray-200">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">Orders</h2>
            </div>
            <div className="max-h-[560px] divide-y divide-gray-200 overflow-y-auto">
              {orders.map((order) => {
                const isSelected = order.id === selectedOrder?.id;
                const statusLabel =
                  ORDER_STATUSES[order.status]?.label || order.status;
                return (
                  <button
                    key={order.id}
                    onClick={() => setSelectedOrder(order)}
                    className={`block w-full p-4 text-left hover:bg-gray-50 ${
                      isSelected ? "bg-primary-50" : "bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="break-words text-sm font-semibold text-gray-900">
                          {order.order_number || order.id}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${statusClass(order.status)}`}
                      >
                        {statusLabel}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-medium text-gray-900">
                      Rs {order.total}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-w-0 space-y-5">
            {selectedOrder && (
              <>
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase text-gray-500">
                        Selected Order
                      </p>
                      <h2 className="mt-1 break-words text-base font-semibold text-gray-900">
                        {selectedOrder.order_number || selectedOrder.id}
                      </h2>
                      <p className="mt-1 text-sm text-gray-500">
                        {formatDate(selectedOrder.created_at)}
                      </p>
                    </div>
                    <span
                      className={`w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass(selectedOrder.status)}`}
                    >
                      {ORDER_STATUSES[selectedOrder.status]?.label ||
                        selectedOrder.status}
                    </span>
                  </div>

                  <div className="mt-4 divide-y divide-gray-200 rounded-lg border border-gray-200">
                    {(selectedOrder.items || []).map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start justify-between gap-3 p-3 text-sm"
                      >
                        <span className="min-w-0 flex-1 break-words text-gray-700">
                          {item.name}
                        </span>
                        <span className="shrink-0 text-gray-500">
                          x{item.quantity}
                        </span>
                        <span className="shrink-0 font-medium text-gray-900">
                          Rs {item.price}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <p className="text-xs text-gray-500">Payment</p>
                      <p className="font-medium text-gray-900">
                        {selectedOrder.payment_method}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="font-medium text-gray-900">
                        Rs {selectedOrder.total}
                      </p>
                    </div>
                  </div>

                  {selectedOrder.shipping_address && (
                    <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm">
                      <p className="font-medium text-gray-900">
                        {selectedOrder.shipping_address.fullName}
                      </p>
                      <p className="mt-1 break-words text-gray-600">
                        {[
                          selectedOrder.shipping_address.addressLine1,
                          selectedOrder.shipping_address.addressLine2,
                          selectedOrder.shipping_address.city,
                          selectedOrder.shipping_address.state,
                          selectedOrder.shipping_address.pincode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-gray-200 p-4">
                  <h2 className="mb-4 text-sm font-semibold text-gray-900">
                    Delivery Progress
                  </h2>
                  <div className="space-y-3">
                    {TIMELINE_STEPS.map((step) => {
                      const completed = currentStep >= step.step;
                      return (
                        <div key={step.step} className="flex items-center gap-3">
                          <span
                            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${
                              completed
                                ? "border-primary-600 bg-primary-600 text-white"
                                : "border-gray-300 bg-white text-gray-500"
                            }`}
                          >
                            {step.step}
                          </span>
                          <span
                            className={`text-sm ${
                              completed ? "text-gray-900" : "text-gray-500"
                            }`}
                          >
                            {step.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackOrderSection;
